import React, { useEffect, useRef, useState } from 'react'
import { Box, ButtonBase, FormHelperText, Typography, type SxProps, type Theme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import MapOutlinedIcon from '@mui/icons-material/MapOutlined'
import MapService, { type EnabledMap } from '../../../services/back4app/MapService'

/**
 * Picks a new company's lobby map as a grid of cards: preview, name and who the layout suits
 * (SC-438, replacing the SC-422 dropdown). Only enabled lobby maps are offered: they have a
 * reception, so every company can receive external guests (rule 3). The first one is picked by
 * default.
 */
export function LobbyMapCards({
  value,
  onChange,
  disabled = false,
  sx,
}: {
  value: string
  onChange: (mapId: string) => void
  disabled?: boolean
  sx?: SxProps<Theme>
}) {
  const [maps, setMaps] = useState<EnabledMap[]>([])
  const [error, setError] = useState('')
  // The list arrives after the first render; picking a default then must use the parent's latest
  // handler, or it would write back the form as it was before the user started typing.
  const latest = useRef({ value, onChange })
  latest.current = { value, onChange }

  useEffect(() => {
    let cancelled = false
    // Company setup is a management screen, so it reads fresh rather than the 60 s memo.
    MapService.listEnabled({ fresh: true })
      .then((list) => {
        if (cancelled) return
        const lobbyMaps = (list || []).filter((m) => m.isLobbyMap)
        setMaps(lobbyMaps)
        if (!latest.current.value && lobbyMaps[0]) latest.current.onChange(lobbyMaps[0].mapId)
        if (lobbyMaps.length === 0) setError('No lobby maps are available yet. Ask a platform admin.')
      })
      .catch(() => !cancelled && setError('Could not load the lobby maps.'))
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <Box sx={sx}>
      <Box
        role="radiogroup"
        aria-label="Office layout"
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))' },
        }}
      >
        {maps.map((map) => {
          const selected = map.mapId === value
          return (
            <ButtonBase
              key={map.mapId}
              role="radio"
              aria-checked={selected}
              disabled={disabled}
              onClick={() => onChange(map.mapId)}
              data-testid={`lobby-map-card-${map.mapId}`}
              sx={(theme) => ({
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                textAlign: 'left',
                borderRadius: 2,
                overflow: 'hidden',
                border: '2px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : 'background.paper',
                transition: 'border-color 120ms',
                '&:hover': { borderColor: selected ? 'primary.main' : alpha(theme.palette.primary.main, 0.5) },
                '&.Mui-focusVisible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
              })}
            >
              <Box
                sx={{
                  aspectRatio: '16 / 9',
                  maxWidth: '100%',
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                }}
              >
                {map.thumbnailUrl ? (
                  <Box
                    component="img"
                    src={map.thumbnailUrl}
                    alt=""
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <MapOutlinedIcon sx={{ fontSize: 40 }} aria-hidden />
                )}
              </Box>
              <Box sx={{ p: 1.5 }}>
                <Typography fontWeight={700} fontSize={15}>
                  {map.name}
                </Typography>
                {map.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {map.description}
                  </Typography>
                )}
              </Box>
            </ButtonBase>
          )
        })}
      </Box>
      {error && (
        <FormHelperText error sx={{ mt: 1 }}>
          {error}
        </FormHelperText>
      )}
    </Box>
  )
}

export default LobbyMapCards
